import type { FC } from "react";
import Image from "components/Image";
import clsx from "clsx";
import { Loading } from "components";
import React, { useMemo, useState, Fragment, useEffect } from "react";
import { generateImage } from "utils/newFunction";
import { useLocale, useWindowSize } from "hooks";
import { ApiService } from "services/serverInClient";
import translation from "./translation";
import style from "./style.module.scss";

const Border = () => (
  <svg
    width="2"
    height="22"
    viewBox="0 0 2 22"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="last:tw-hidden"
  >
    <rect x="0.5" width="1" height="22" fill="#344456" fillOpacity="0.2" />
  </svg>
);

const combineData = (arr) => {
  const result = [];
  const mobileResult = [];
  const formatted = arr
    ?.map?.((item) => ({
      src: generateImage(item?.UserImage?.value),
      priority: item?.UserPriority?.value || "",
      title: item?.UserName?.value || "",
      email: item?.UserEmail?.value || "",
      tel:
        (Array.isArray(item.UserPhone?.value)
          ? item?.UserPhone?.value?.[0]
          : item?.UserPhone?.value) || "",
      post: item?.UserSide?.value || "",
      place: item?.UserLocation?.value || "",
      alt: item?.imageTag?.value || item?.UserName?.value || "",
      degree: item?.UserDegree?.value || "",
      field: item?.UserField?.value || "",
    }))
    ?.sort((a, b) => a?.priority - b?.priority);

  const chunker = (array) => {
    result?.push?.(array.splice(0, formatted.length === 4 ? 2 : 3));
    if (array.length > 0) {
      chunker(array);
    }
  };
  const mobileChunker = (array) => {
    mobileResult?.push?.(array.splice(0, 2));
    if (array.length > 0) {
      mobileChunker(array);
    }
  };
  const cloned = JSON.parse(JSON.stringify(formatted));
  const mobileCloned = JSON.parse(JSON.stringify(formatted));

  chunker(cloned);
  mobileChunker(mobileCloned);
  return [result, mobileResult];
};

interface IProps {
  data?: any;
  personsTagTree?: string;
  title?: string;
}

const PersonsComponent: FC<IProps> = ({
  data,
  personsTagTree = null,
  title = null,
}) => {
  const [people, setPeople] = useState([]);
  const [listData, mobileListData] = useMemo(
    () => combineData(people),
    [people]
  );
  const [meta, setMeta] = useState([]);
  const [rowCol, setRowCol] = useState("");
  const { locale, isRTL } = useLocale();
  const [loading, setLoading] = useState(false);
  const { width } = useWindowSize();
  const [isFreezedMode, setIsFreezedMode] = useState<boolean>(false);

  useEffect(() => {
    if (personsTagTree) {
      (async () => {
        try {
          setLoading(true);
          const apiService = new ApiService();
          const response = await apiService.getContent({
            tagTrees: [personsTagTree],
            orderBy: ["content.UserPriority:asc"],
          });
          setPeople(response);
        } finally {
          setLoading(false);
        }
      })();
    }
  }, [personsTagTree]);

  const onMouseEnter = (el, i: number, j: number) => {
    if (!isFreezedMode) {
      const obj = { [i]: el };
      setRowCol(`${i}${j}`);
      setMeta(obj);
    }
  };

  const onMouseLeave = () => {
    if (!isFreezedMode) {
      setRowCol("");
      setMeta([]);
    }
  };

  const onOutsideClick = (ev) => {
    setRowCol("");
    setMeta([]);
    setIsFreezedMode(false);
    ev?.stopPropagation?.();
  };

  const onImageClick = (
    ev: React.MouseEvent<HTMLElement>,
    el,
    i: number,
    j: number
  ) => {
    if (isFreezedMode && rowCol === `${i}${j}`) {
      onOutsideClick({});
      return;
    }

    const obj = { [i]: el };
    setRowCol(`${i}${j}`);
    setMeta(obj);
    setIsFreezedMode(true);
    ev?.stopPropagation?.();
  };

  useEffect(() => {
    setPeople(data?.fetch?.people || []);
  }, [data?.fetch?.people]);

  useEffect(() => {
    const personCards = document.querySelectorAll('[data-tag="person-card"]');
    const cb = (ev) => {
      let isFounded = false;
      personCards.forEach((item) => {
        if (item.contains(ev.target)) {
          isFounded = true;
        }
      });
      if (!isFounded) {
        onOutsideClick(ev);
      }
    };

    document.addEventListener("click", cb);
    return () => {
      return document.removeEventListener("click", cb);
    };
  }, []);

  if (loading) {
    return (
      <div className="tw-bg-white tw-rounded-2xl tw-border tw-border-solid tw-border-[#E5E9F4] tw-py-10 tw-flex tw-w-full tw-justify-center">
        <Loading />
      </div>
    );
  }

  return (
    <div
      className="tw-bg-white tw-rounded-2xl tw-border tw-border-solid tw-border-[#E5E9F4] tw-py-10 tw-relative"
      // onClick={onOutsideClick}
    >
      {title && <h3 className={"tw-px-5 -tw-mt-5 tw-text-base"}>{title}</h3>}
      {[listData, mobileListData][744 > width && 414 < width ? 1 : 0]?.map?.(
        (item, i) => (
          <Fragment key={i}>
            <div
              className={clsx(
                "tw-grid tw-grid-cols-1 tw-gap-y-6 tw-mt-6 first:tw-mt-0 md:tw-px-[100px] tw-w-full",
                {
                  "md:tw-grid-cols-3": item.length === 3,
                  "sm:tw-grid-cols-2": item.length === 2,
                  "md:tw-grid-cols-1": item.length === 1,
                }
              )}
            >
              {item?.map?.((el, j) => (
                <div
                  key={j}
                  className={clsx(
                    style["person-card"],
                    "tw-flex tw-flex-col tw-items-center tw-group tw-relative"
                  )}
                  data-tag="person-card"
                >
                  <Image
                    height={150}
                    width={150}
                    className="tw-rounded-full tw-object-cover tw-w-[150px] xxxl:tw-w-[200px] tw-h-[150px] xxxl:tw-h-[200px] hover:tw-cursor-pointer"
                    src={el.src}
                    alt={el.alt}
                    onMouseEnter={() => onMouseEnter(el, i, j)}
                    onMouseLeave={() => onMouseLeave()}
                    onClick={(ev) => onImageClick(ev, el, i, j)}
                    loading="eager"
                  />
                  <h6 className="tw-mt-5 tw-text-[14px] xxxl:tw-text-[18.65px] tw-font-medium tw-text-[#344456]">
                    {el.title}
                  </h6>
                  {rowCol === `${i}${j}` && (
                    <span
                      className="tw-w-5 tw-h-5 tw-absolute tw-top-[115%] md:tw-top-[110%]"
                      style={{
                        borderBottom: "20px solid #E5E9F4",
                        borderRight: "20px solid transparent",
                        borderLeft: "20px solid transparent",
                      }}
                    />
                  )}
                  <div className="tw-block md:tw-hidden tw-py-4 tw-bg-[#E5E9F4] tw-text-[#344456] tw-text-sm xxxl:tw-text-[17.33px] md:tw-px-[50px] tw-w-full tw-mt-10 tw-relative">
                    <div className="tw-absolute -tw-top-4 tw-flex tw-justify-center tw-w-full tw-mx-auto">
                      <span
                        className="tw-w-5 tw-h-5"
                        style={{
                          borderBottom: "20px solid #E5E9F4",
                          borderRight: "20px solid transparent",
                          borderLeft: "20px solid transparent",
                        }}
                      />
                    </div>
                    {el?.post && (
                      <div className="tw-mb-2 last:tw-mb-0 tw-px-4">
                        <b>{translation[locale]?.POST}: </b>
                        <span>{el?.post}</span>
                      </div>
                    )}
                    {el?.degree && (
                      <div className="tw-mb-2 last:tw-mb-0 tw-px-4">
                        <b>{translation[locale]?.DEGREE}: </b>
                        <span>{el?.degree}</span>
                      </div>
                    )}
                    {el?.field && (
                      <div className="tw-mb-2 last:tw-mb-0 tw-px-4">
                        <b>{translation[locale]?.FIELD}: </b>
                        <span>{el?.field}</span>
                      </div>
                    )}

                    {el?.place && (
                      <div className="tw-mb-2 last:tw-mb-0 tw-px-4 tw-flex tw-items-center">
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M10.0007 1.66669C12.759 1.66669 15.0007 3.88335 15.0007 6.62502C15.0007 10.3417 10.0007 15.8334 10.0007 15.8334C10.0007 15.8334 5.00065 10.3417 5.00065 6.62502C5.00065 3.88335 7.24232 1.66669 10.0007 1.66669ZM10.0007 5.00002C9.55862 5.00002 9.1347 5.17561 8.82214 5.48818C8.50958 5.80074 8.33398 6.22466 8.33398 6.66669C8.33398 7.10871 8.50958 7.53264 8.82214 7.8452C9.1347 8.15776 9.55862 8.33335 10.0007 8.33335C10.4427 8.33335 10.8666 8.15776 11.1792 7.8452C11.4917 7.53264 11.6673 7.10871 11.6673 6.66669C11.6673 6.22466 11.4917 5.80074 11.1792 5.48818C10.8666 5.17561 10.4427 5.00002 10.0007 5.00002ZM16.6673 15.8334C16.6673 17.675 13.684 19.1667 10.0007 19.1667C6.31732 19.1667 3.33398 17.675 3.33398 15.8334C3.33398 14.7584 4.35065 13.8 5.92565 13.1917L6.45898 13.95C5.55898 14.325 5.00065 14.8417 5.00065 15.4167C5.00065 16.5667 7.24232 17.5 10.0007 17.5C12.759 17.5 15.0007 16.5667 15.0007 15.4167C15.0007 14.8417 14.4423 14.325 13.5423 13.95L14.0757 13.1917C15.6507 13.8 16.6673 14.7584 16.6673 15.8334Z"
                            fill="#344456"
                          />
                        </svg>

                        <span
                          className={clsx({
                            "tw-mr-2": isRTL,
                            "tw-ml-2": !isRTL,
                          })}
                        >
                          {el?.place}
                        </span>
                      </div>
                    )}

                    {el?.tel && (
                      <div className="tw-mb-2 last:tw-mb-0 tw-px-4 tw-flex tw-items-center">
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          style={isRTL ? { transform: "scaleX(-1)" } : {}}
                        >
                          <path
                            d="M5.51667 8.99167C6.71667 11.35 8.65 13.2833 11.0083 14.4833L12.8417 12.65C13.075 12.4167 13.4 12.35 13.6917 12.4417C14.625 12.75 15.625 12.9167 16.6667 12.9167C17.125 12.9167 17.5 13.2917 17.5 13.75V16.6667C17.5 17.125 17.125 17.5 16.6667 17.5C8.84167 17.5 2.5 11.1583 2.5 3.33333C2.5 2.875 2.875 2.5 3.33333 2.5H6.25C6.70833 2.5 7.08333 2.875 7.08333 3.33333C7.08333 4.375 7.25 5.375 7.55833 6.30833C7.65 6.6 7.58333 6.925 7.35 7.15833L5.51667 8.99167Z"
                            fill="#344456"
                          />
                        </svg>
                        <a
                          href={`tel:${el?.tel}`}
                          className={clsx({
                            "tw-mr-2": isRTL,
                            "tw-ml-2": !isRTL,
                          })}
                        >
                          {el?.tel}
                        </a>
                      </div>
                    )}

                    {el?.email && (
                      <div className="tw-mb-2 last:tw-mb-0 tw-px-4 tw-flex tw-items-center">
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M16.6665 6.66665L9.99984 10.8333L3.33317 6.66665V4.99998L9.99984 9.16665L16.6665 4.99998M16.6665 3.33331H3.33317C2.40817 3.33331 1.6665 4.07498 1.6665 4.99998V15C1.6665 15.442 1.8421 15.8659 2.15466 16.1785C2.46722 16.4911 2.89114 16.6666 3.33317 16.6666H16.6665C17.1085 16.6666 17.5325 16.4911 17.845 16.1785C18.1576 15.8659 18.3332 15.442 18.3332 15V4.99998C18.3332 4.07498 17.5832 3.33331 16.6665 3.33331Z"
                            fill="#344456"
                          />
                        </svg>

                        <a
                          href={`mailto:${el?.email}`}
                          className={clsx({
                            "tw-mr-2": isRTL,
                            "tw-ml-2": !isRTL,
                          })}
                        >
                          {el?.email}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div
              onClick={(ev) => ev.stopPropagation()}
              className={clsx(
                "tw-hidden md:tw-flex tw-justify-between tw-flex-wrap tw-py-4 tw-bg-[#E5E9F4] tw-text-[#344456] tw-text-sm tw-w-full tw-mt-10 tw-min-h-[54px] tw-px-[50px]",
                { "tw-opacity-0": !meta?.[i] }
              )}
            >
              {meta?.[i]?.post && (
                <>
                  <div
                    className={clsx(
                      "tw-flex tw-items-center first:tw-justify-start last:tw-justify-end tw-justify-center tw-border-solid tw-border-0 tw-border-[#344456]/20"
                    )}
                  >
                    <div
                      className={clsx({
                        "tw-text-right": isRTL,
                        "tw-text-left": !isRTL,
                      })}
                    >
                      <b>{translation[locale]?.POST}: </b>
                      <span>
                        <b>{meta?.[i]?.post}</b>
                      </span>
                    </div>
                  </div>
                  <Border />
                </>
              )}

              {meta?.[i]?.degree && (
                <>
                  <div
                    className={clsx(
                      "tw-flex tw-items-center first:tw-justify-start last:tw-justify-end tw-justify-center tw-border-solid tw-border-0 tw-border-[#344456]/20"
                    )}
                  >
                    <div
                      className={clsx({
                        "tw-text-right": isRTL,
                        "tw-text-left": !isRTL,
                      })}
                    >
                      <b>{translation[locale]?.DEGREE}: </b>
                      <span>
                        <b>{meta?.[i]?.degree}</b>
                      </span>
                    </div>
                  </div>
                  <Border />
                </>
              )}
              {meta?.[i]?.field && (
                <>
                  <div
                    className={clsx(
                      "tw-flex tw-items-center first:tw-justify-start last:tw-justify-end tw-justify-center tw-border-solid tw-border-0 tw-border-[#344456]/20"
                    )}
                  >
                    <div
                      className={clsx({
                        "tw-text-right": isRTL,
                        "tw-text-left": !isRTL,
                      })}
                    >
                      <b>{translation[locale]?.FIELD}: </b>
                      <span>
                        <b>{meta?.[i]?.field}</b>
                      </span>
                    </div>
                  </div>
                  <Border />
                </>
              )}

              {meta?.[i]?.tel && (
                <>
                  <div
                    className={clsx(
                      "tw-flex tw-items-center first:tw-justify-start last:tw-justify-end tw-justify-center tw-text-end tw-border-solid tw-border-0 tw-border-[#344456]/20"
                    )}
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      // className={clsx({ '-tw-scale-1': isRTL })}
                      style={isRTL ? { transform: "scaleX(-1)" } : {}}
                    >
                      <path
                        d="M5.51667 8.99167C6.71667 11.35 8.65 13.2833 11.0083 14.4833L12.8417 12.65C13.075 12.4167 13.4 12.35 13.6917 12.4417C14.625 12.75 15.625 12.9167 16.6667 12.9167C17.125 12.9167 17.5 13.2917 17.5 13.75V16.6667C17.5 17.125 17.125 17.5 16.6667 17.5C8.84167 17.5 2.5 11.1583 2.5 3.33333C2.5 2.875 2.875 2.5 3.33333 2.5H6.25C6.70833 2.5 7.08333 2.875 7.08333 3.33333C7.08333 4.375 7.25 5.375 7.55833 6.30833C7.65 6.6 7.58333 6.925 7.35 7.15833L5.51667 8.99167Z"
                        fill="#344456"
                      />
                    </svg>

                    <a
                      href={`tel:${meta?.[i]?.tel}`}
                      className={clsx({
                        "tw-mr-2": isRTL,
                        "tw-ml-2": !isRTL,
                      })}
                    >
                      {meta?.[i]?.tel}
                    </a>
                  </div>
                  <Border />
                </>
              )}

              {meta?.[i]?.place && (
                <>
                  <div
                    className={clsx(
                      "tw-flex tw-items-center first:tw-justify-start last:tw-justify-end tw-justify-center tw-text-end tw-border-solid tw-border-0 tw-border-[#344456]/20 tw-truncate "
                    )}
                  >
                    <div
                      className={clsx(
                        "tw-flex tw-items-center tw-justify-end tw-text-end"
                      )}
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M10.0007 1.66669C12.759 1.66669 15.0007 3.88335 15.0007 6.62502C15.0007 10.3417 10.0007 15.8334 10.0007 15.8334C10.0007 15.8334 5.00065 10.3417 5.00065 6.62502C5.00065 3.88335 7.24232 1.66669 10.0007 1.66669ZM10.0007 5.00002C9.55862 5.00002 9.1347 5.17561 8.82214 5.48818C8.50958 5.80074 8.33398 6.22466 8.33398 6.66669C8.33398 7.10871 8.50958 7.53264 8.82214 7.8452C9.1347 8.15776 9.55862 8.33335 10.0007 8.33335C10.4427 8.33335 10.8666 8.15776 11.1792 7.8452C11.4917 7.53264 11.6673 7.10871 11.6673 6.66669C11.6673 6.22466 11.4917 5.80074 11.1792 5.48818C10.8666 5.17561 10.4427 5.00002 10.0007 5.00002M16.6673 15.8334C16.6673 17.675 13.684 19.1667 10.0007 19.1667C6.31732 19.1667 3.33398 17.675 3.33398 15.8334C3.33398 14.7584 4.35065 13.8 5.92565 13.1917L6.45898 13.95C5.55898 14.325 5.00065 14.8417 5.00065 15.4167C5.00065 16.5667 7.24232 17.5 10.0007 17.5C12.759 17.5 15.0007 16.5667 15.0007 15.4167C15.0007 14.8417 14.4423 14.325 13.5423 13.95L14.0757 13.1917C15.6507 13.8 16.6673 14.7584 16.6673 15.8334Z"
                          fill="#344456"
                        />
                      </svg>

                      <span
                        className={clsx("tw-overflow-hidden tw-max-h-[50px]", {
                          "tw-mr-2": isRTL,
                          "tw-ml-2": !isRTL,
                        })}
                      >
                        {meta?.[i]?.place}
                      </span>
                    </div>
                  </div>
                  <Border />
                </>
              )}

              {meta?.[i]?.email && (
                <>
                  <div
                    className={clsx(
                      "tw-flex tw-items-center first:tw-justify-start last:tw-justify-end tw-justify-center tw-text-end tw-border-solid tw-border-0 tw-border-[#344456]/20"
                    )}
                  >
                    <div
                      className={clsx(
                        "tw-flex tw-items-center tw-justify-end tw-text-end",
                        {
                          "tw-pl-[50px]": isRTL,
                          "tw-pr-[50px]": !isRTL,
                        }
                      )}
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M16.666 6.66665L9.99935 10.8333L3.33268 6.66665V4.99998L9.99935 9.16665L16.666 4.99998M16.666 3.33331H3.33268C2.40768 3.33331 1.66602 4.07498 1.66602 4.99998V15C1.66602 15.442 1.84161 15.8659 2.15417 16.1785C2.46673 16.4911 2.89065 16.6666 3.33268 16.6666H16.666C17.108 16.6666 17.532 16.4911 17.8445 16.1785C18.1571 15.8659 18.3327 15.442 18.3327 15V4.99998C18.3327 4.07498 17.5827 3.33331 16.666 3.33331Z"
                          fill="#344456"
                        />
                      </svg>

                      <a
                        href={`mailto:${meta?.[i]?.email}`}
                        className={clsx({
                          "tw-mr-2": isRTL,
                          "tw-ml-2": !isRTL,
                        })}
                      >
                        {meta?.[i]?.email}
                      </a>
                    </div>
                  </div>
                  <Border />
                </>
              )}
            </div>
          </Fragment>
        )
      )}
    </div>
  );
};

export default PersonsComponent;
